import React from 'react';

interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  activeEntity: string;
  onEntityChange: (entity: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

const menuItems = [
  { id: 'executive', icon: '🎯', label: 'Executive Summary' },
  { id: 'dashboard', icon: '📊', label: 'Dashboard' },
  { id: 'revenue', icon: '💰', label: 'Revenue' },
  { id: 'expenses', icon: '📉', label: 'Expenses' },
  { id: 'profit_loss', icon: '📈', label: 'Profit and Loss' },
  { id: 'balance', icon: '⚖️', label: 'Balance Sheet' },
  { id: 'cashflow', icon: '💵', label: 'Cash Flow' },
  { id: 'gl_lookup', icon: '📖', label: 'GL Lookup' },
  { id: 'database', icon: '🗄️', label: 'Database' },
];

const reportItems = [
  { id: 'forecast', icon: '🔮', label: 'Forecast' },
  { id: 'budget_plan', icon: '📋', label: 'Budget Plan' },
];

const analysisItems = [
  { id: 'bep', icon: '📈', label: 'BEP Status' },
  { id: 'analytics', icon: '🔬', label: 'Analytic Tools' },
];

const Sidebar: React.FC<SidebarProps> = ({ 
  activeSection, 
  onSectionChange, 
  activeEntity, 
  onEntityChange,
  isCollapsed,
  onToggleCollapse
}) => {
  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Dynamic Profile Switcher Header */}
      <div className="sidebar-header" style={{ paddingBottom: '16px', borderBottom: '1px solid var(--border-color)', marginBottom: '12px' }}>
        <div className="sidebar-logo-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', width: '100%' }}>
          <div className="sidebar-logo" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src="/logo-juara-gadai.png"
              alt="Juara Gadai"
              style={{
                width: '36px',
                height: '36px',
                objectFit: 'contain',
                borderRadius: '6px',
              }}
            />
            <div className="sidebar-logo-text">
              <span className="sidebar-logo-title" style={{ fontSize: '15px', fontWeight: '800', color: 'var(--text-primary)' }}>
                {activeEntity === 'JUARA_GADAI' ? 'Juara Gadai' : 'Sahabat Gadai'}
              </span>
              <span className="sidebar-logo-subtitle" style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                {activeEntity === 'JUARA_GADAI' ? 'Juara Gadai Group' : 'JGJB Finance'}
              </span>
            </div>
          </div>
          <button
            onClick={onToggleCollapse}
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid var(--border-color)',
              borderRadius: '50%',
              color: 'var(--text-secondary)',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              outline: 'none',
              fontSize: '10px',
            }}
            className="sidebar-collapse-btn"
            title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            {isCollapsed ? '▶' : '◀'}
          </button>
        </div>

        {/* Custom Profile Dropdown */}
        <div className="profile-switcher" style={{ position: 'relative', width: '100%' }}>
          <style dangerouslySetInnerHTML={{__html: `
            .profile-select:hover {
              background-color: rgba(255, 255, 255, 0.06) !important;
              border-color: var(--emerald) !important;
            }
            .profile-select:focus {
              border-color: var(--emerald) !important;
              box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.15) !important;
            }
            .profile-select-jg:hover {
              border-color: #F68B1F !important;
            }
            .profile-select-jg:focus {
              border-color: #F68B1F !important;
              box-shadow: 0 0 0 2px rgba(246, 139, 31, 0.20) !important;
            }
          `}} />
          <select
            value={activeEntity}
            onChange={(e) => onEntityChange(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 34px',
              fontSize: '12px',
              fontWeight: '700',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-light)',
              backgroundColor: 'rgba(255,255,255,0.02)',
              color: 'var(--text-primary)',
              outline: 'none',
              cursor: 'pointer',
              appearance: 'none',
              backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'rgba(255,255,255,0.5)\' stroke-width=\'2.5\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
              backgroundSize: '12px',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            className={`profile-select ${activeEntity === 'JUARA_GADAI' ? 'profile-select-jg' : ''}`}
          >
            <option value="JUARA_GADAI" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)' }}>
              ⚡ Juara Gadai Group (Primary)
            </option>
            <option value="SAHABAT_GADAI" style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)' }}>
              💼 Sahabat Gadai
            </option>
          </select>
          <span style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            fontSize: '11px',
            pointerEvents: 'none'
          }}>
            {activeEntity === 'JUARA_GADAI' ? '🟠' : '🟢'}
          </span>
        </div>
      </div>

      <nav className="sidebar-nav">
        <div className="sidebar-section-title">Menu Utama</div>
        {menuItems.map(item => (
          <div
            key={item.id}
            className={`sidebar-item ${activeSection === item.id ? 'active' : ''}`}
            onClick={() => onSectionChange(item.id)}
          >
            <span className="sidebar-item-icon">{item.icon}</span>
            <span>{item.label}</span>
          </div>
        ))}

        <div className="sidebar-section-title">Laporan</div>
        {reportItems.map(item => (
          <div
            key={item.id}
            className={`sidebar-item ${activeSection === item.id ? 'active' : ''}`}
            onClick={() => onSectionChange(item.id)}
          >
            <span className="sidebar-item-icon">{item.icon}</span>
            <span>{item.label}</span>
          </div>
        ))}

        <div className="sidebar-section-title">Analisis</div>
        {analysisItems.map(item => (
          <div
            key={item.id}
            className={`sidebar-item ${activeSection === item.id ? 'active' : ''}`}
            onClick={() => onSectionChange(item.id)}
          >
            <span className="sidebar-item-icon">{item.icon}</span>
            <span>{item.label}</span>
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div className="sidebar-avatar" style={{ backgroundColor: activeEntity === 'JUARA_GADAI' ? '#F68B1F' : 'var(--emerald)', flexShrink: 0 }}>FA</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">Finance Admin</div>
            <div className="sidebar-user-role">Administrator</div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
